import { Entity } from '@common/types/Entity';
import { EntityEvents } from '@common/user-analytics';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { EventName, EventType, metrics } from '@prophecy/metrics';
import { theme } from '@prophecy/theme';
import { Ellipsis, overlayStyle, Stack, StackItem, Text } from '@prophecy/ui-v3';
import { Alert } from '@prophecy/ui-v3/ui-compat';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { useMixPanel } from '../../../../context/mixpanel/context';
import { FileFormatLabels, FileFormats, StatusMapKeys, Transpiler_Headline } from '../../config';
import { useImportState } from '../useImportState';
import { StyledText, UploadPercentageCard } from './components/UploadPercentageCard';
import { StyledStackContainer, StyledWhiteCard } from './styled';
import { PipelineStatusType } from './types';

const StyledAlert = styled(Alert)`
  position: absolute;
  top: -1px;
`;

const MessageCard = ({ transpilationStatus, children = [], ...rest }: PipelineStatusType & { isOverAll?: boolean }) => {
  const cardChildren = children.map((child) => {
    const { transpilationStatus: status, ...restProps } = child;
    return {
      ...restProps,
      status: status,
      isError: status === StatusMapKeys.error || status === StatusMapKeys.failed,
      isSuccess: status === StatusMapKeys.completed || restProps.percent > 99
    };
  });
  return (
    <UploadPercentageCard
      {...rest}
      hasArrowIcon={true}
      status={transpilationStatus}
      isError={transpilationStatus === StatusMapKeys.error || transpilationStatus === StatusMapKeys.failed}
      isSuccess={transpilationStatus === StatusMapKeys.completed || rest.percent > 99}>
      {cardChildren}
    </UploadPercentageCard>
  );
};
export const TranspileGraph = () => {
  const {
    transpileGraphState: {
      clientState: {
        transpileSummary: { statuses, isError, estimationTime, isSuccess },
        savedEntities: { isConfigGeneration }
      }
    },
    transpilerState: { transpilerId },
    formDataWithActions: {
      formContext: { getValues }
    }
  } = useImportState();
  const { source } = getValues() as { source: FileFormats };

  const isSql = getValues('language') === BackEndCodeLanguage.sql;

  const { track } = useMixPanel();
  useEffect(() => {
    track(EntityEvents.transpile.home.transpilationStarted, {
      transpilerId
    });
    metrics.track({
      eventName: EventName.ui.transpiler.import.transpilation.start,
      eventType: EventType.userAction,
      payload: { transpilerId }
    });
  }, [track, transpilerId]);

  let overAllPercent = 0;

  let errorMsg = undefined;
  const errorStatuses = statuses.filter(
    (status) => status.transpilationStatus === 'failed' || status.transpilationStatus === 'error'
  );
  const isOverAllError = (statuses.length > 0 && errorStatuses.length === statuses.length) || isError;
  errorMsg =
    errorStatuses?.[errorStatuses.length - 1]?.message || (isError ? statuses[statuses.length - 1]?.message : '');
  statuses.forEach((status) => {
    overAllPercent += status.percent || 0;
  });
  const queueCount = statuses.length;

  const headingText = isConfigGeneration ? 'Config generation' : 'Prophecy migration';
  let heading = `${
    estimationTime ? `Estimated time: ${estimationTime}.` : ''
  } Please wait while we are working on ${headingText}.`;

  if (isOverAllError) {
    heading = `${headingText} failed.`;
  } else if (isSuccess) {
    heading = `${headingText} done successfully.`;
  }

  return (
    <Stack
      width='100%'
      height='100%'
      alignY='center'
      padding={`${theme.spaces.x64} ${theme.spaces.x32} ${theme.spaces.x48}`}>
      {errorMsg && (
        <StyledAlert variant='error' showIcon banner={true}>
          <StyledText level='xs' tone={theme.colors.error700}>
            <Ellipsis tooltip={true} tooltipProps={{ overlayStyle }}>
              {errorMsg}
            </Ellipsis>
          </StyledText>
        </StyledAlert>
      )}
      <StyledWhiteCard>
        <Stack gap={theme.spaces.x10} width='100%' height='100%'>
          <Text level='xl' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
            Transpiling {FileFormatLabels[source]} to {isSql ? 'SQL' : 'Spark'}
          </Text>
          <Text
            level='sm13'
            weight={theme.fontWeight.normal}
            tone={isOverAllError ? theme.colors.error700 : theme.colors.gray700}>
            {heading}
          </Text>

          <StackItem grow='1'>
            <Stack height='100%' width='100%' gap={theme.spaces.x20} padding={`${theme.spaces.x40} 0 0`}>
              <Text level='md' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
                Overall Progress
              </Text>
              <MessageCard
                entity={Entity.Transpiler}
                name={Transpiler_Headline}
                isOverAll={true}
                id='all'
                transpilationStatus={isOverAllError ? StatusMapKeys.error : StatusMapKeys.running}
                percent={overAllPercent / statuses?.length || 0}
              />
              {statuses.length > 0 && (
                <StackItem grow='1'>
                  <StyledStackContainer
                    height='100%'
                    width='100%'
                    gap={theme.spaces.x20}
                    padding={`${theme.spaces.x20} 0 0`}>
                    <Text level='md' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
                      Queue {queueCount > 0 ? `(${queueCount})` : ''}
                    </Text>
                    {statuses.map((transpiledStatus, index) => {
                      let currentEntity = transpiledStatus.entity;
                      return <MessageCard key={index} {...transpiledStatus} entity={currentEntity} />;
                    })}
                  </StyledStackContainer>
                </StackItem>
              )}
            </Stack>
          </StackItem>
        </Stack>
      </StyledWhiteCard>
    </Stack>
  );
};
